import { _ } from "../../../polyglot/polyglot.js";
import { getPeriodTypeFromId } from "../../../../models/period/PeriodType.js";
import { makeDropdown } from "../../makeRoadmapStepContent.js";
import {
	notBlank,
	validateAll,
	validatorWithLastError,
	validInteger,
} from "../../../widgets/forms/validators.js";
import AbstractRoadmapStep from "../../AbstractRoadmapStep.js";
import inputFieldWidget from "../../../widgets/forms/fields/inputFieldWidget.js";

/**
 * This allows the user to choose VAT payment frequency and due date.
 */
export default class VatFlowStep extends AbstractRoadmapStep {
	constructor() {
		super(...arguments);

		this._validator = validatorWithLastError(
			validateAll(notBlank(), validInteger()),
		);
	}

	getTitle() {
		return _("VAT Flow");
	}

	_getSubtitle() {
		return _(
			"What is your company's VAT frequency? How long after the VAT period do your pay VAT?",
		);
	}

	_getStepContent() {
		return (html) =>
			html.div(
				{
					class: "wizard-step-dropdown",
					"data-test": "wizard-vat-flow-step",
				},
				[
					makeDropdown({
						title: _("Period frequency"),
						dataTestName: "vat-period-frequency",
						items: this._getVatPaymentPeriods(),
						helpText: _(
							"Choose the VAT period frequency, which you report to the tax authority.",
						),
						onChange: (selection) => {
							this._context.configuration.setVatPaymentPeriod(
								selection.id,
							);

							this._updateVatPaymentLatency({
								periodTypeId: selection.id,
							});
						},
					}),
				],
				this._makeInput(),
			);
	}

	_getVatPaymentPeriods() {
		return this._context.vatPaymentSettings.map((vatPaymentSetting) => {
			const periodType = getPeriodTypeFromId(
				vatPaymentSetting.getVatPaymentPeriod(),
			);

			return {
				name: periodType.getName(),
				id: periodType.getId(),
				selected:
					this._context.configuration.getVatPaymentPeriod() ===
					periodType.getId(),
			};
		});
	}

	_updateVatPaymentLatency({ periodTypeId }) {
		const vatPeriodSetting = this._context.vatPaymentSettings.find(
			(vatPaymentSetting) =>
				vatPaymentSetting.getVatPaymentPeriod() === periodTypeId,
		);

		this._getInputField().setValue(
			vatPeriodSetting?.getVatPaymentLatency().toString(),
		);
	}

	_makeInput() {
		return (html) =>
			html.div(
				{
					class: "wizard-step-dropdown",
				},
				html.div({ class: "title" }, _("Payment delay")),
				html.div(
					{
						class: "wizard-step-input",
						"data-test": "vat-due-date",
					},
					_("On day {input} of the following month", {
						input: this._getInputField(),
					}),
				),
			);
	}

	_getInputField() {
		if (this._inputFieldWidget) {
			return this._inputFieldWidget;
		}

		this._inputFieldWidget = inputFieldWidget({
			attributes: {
				min: 1,
				required: true,
			},
			type: "number",
			name: "day-of-the-month",
			value: this._context.configuration
				.getVatPaymentLatency()
				.toString(),
			"data-test": "input-field",
			validator: this._validator,
		});

		return this._inputFieldWidget;
	}

	_doSubmit({ onDone }) {
		const inputValue = this._getInputField().getValue();
		const isValid = this._getInputField().isValid(inputValue);
		if (!isValid) {
			this.addSubmitError(this._validator.getLastError());
			onDone();
			return;
		}
		this._context.configuration.setVatPaymentLatency(+inputValue);
		onDone();
	}
}
